import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiCheckUsername, apiForgotPassword, apiResendConfirmation } from "../services/authService";
import { USERNAME_RE } from "../utils/validation";
import "../App.css";

const CONTEXT_MESSAGES = {
  battle: "Sign in or create a free account to generate this battle.",
};

export default function AuthModal({ isOpen, onClose, contextReason }) {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState("signin"); // "signin" | "signup" | "forgot"
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState("");

  // Username availability state
  const [usernameStatus, setUsernameStatus] = useState("idle"); // "idle"|"checking"|"available"|"taken"|"invalid"
  const debounceRef = useRef(null);
  const firstInputRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Reset everything on open — always start fresh on sign in
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      setMode("signin");
      setError("");
      setLoading(false);
      setPendingEmail(null);
      setUsername("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setRulesAccepted(false);
      setUsernameStatus("idle");
      setShowResend(false);
      setResendSent(false);
      setResendLoading(false);
      setResendError("");
      setTimeout(() => firstInputRef.current?.focus(), 50);
    } else {
      previousFocusRef.current?.focus?.();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    setError("");
    setUsername("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setRulesAccepted(false);
    setUsernameStatus("idle");
    setShowResend(false);
    setResendSent(false);
    setResendError("");
  }, [mode]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // Debounced username availability check
  const checkUsername = useCallback((value) => {
    clearTimeout(debounceRef.current);
    if (!value) { setUsernameStatus("idle"); return; }
    if (!USERNAME_RE.test(value)) { setUsernameStatus("invalid"); return; }
    setUsernameStatus("checking");
    debounceRef.current = setTimeout(async () => {
      try {
        const { available } = await apiCheckUsername(value);
        setUsernameStatus(available ? "available" : "taken");
      } catch {
        setUsernameStatus("idle");
      }
    }, 400);
  }, []);

  function handleUsernameChange(e) {
    const val = e.target.value;
    setUsername(val);
    checkUsername(val);
  }

  if (!isOpen) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setShowResend(false);
    setResendSent(false);
    setResendError("");

    if (mode === "signup") {
      if (!USERNAME_RE.test(username)) {
        setError("Username must be 3–20 characters: letters, numbers, _ or -");
        return;
      }
      if (usernameStatus === "taken") {
        setError("That username is already taken.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
      if (!rulesAccepted) {
        setError("Please acknowledge the Rules & Code of Conduct to continue.");
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === "signin") {
        await signIn(email, password);
        onClose();
      } else if (mode === "signup") {
        const result = await signUp(username, email, password, ["rules"]);
        if (result.pending) setPendingEmail(result.email ?? email);
      } else if (mode === "forgot") {
        await apiForgotPassword(email);
        setPendingEmail(email);
      }
    } catch (err) {
      const msg = err.message ?? "";
      if (msg === "EMAIL_NOT_CONFIRMED") {
        setError("Your email hasn't been confirmed yet.");
        setShowResend(true);
      } else {
        setError(msg || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResendLoading(true);
    setResendError("");
    try {
      await apiResendConfirmation(email);
      setResendSent(true);
    } catch (e) {
      if (e?.errorCode === "rate_limited") {
        setResendError("Please wait a moment before requesting another confirmation email.");
      } else {
        setResendSent(true); // show success anyway — don't reveal if email exists
      }
    } finally {
      setResendLoading(false);
    }
  }

  const usernameHint = {
    idle: null,
    checking: <span className="username-hint checking">Checking…</span>,
    available: <span className="username-hint available">✓ Available</span>,
    taken: <span className="username-hint taken">✗ Already taken</span>,
    invalid: <span className="username-hint invalid">3–20 chars, letters/numbers/_ /-</span>,
  }[usernameStatus];

  return (
    <div className="auth-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="auth-modal-title">
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-close" onClick={onClose} aria-label="Close">✕</button>

        {pendingEmail ? (
          <div className="auth-pending">
            <div className="auth-pending-icon">✉</div>
            <h2 className="auth-title" id="auth-modal-title">Check your inbox</h2>
            <p className="auth-pending-text">
              {mode === "forgot"
                ? <>We sent a password reset link to <strong>{pendingEmail}</strong>. Click it to choose a new password.</>
                : <>We sent a confirmation link to <strong>{pendingEmail}</strong>. Click it to activate your account, then sign in.</>
              }
            </p>
            <button className="auth-toggle-link" onClick={() => { setPendingEmail(null); setMode("signin"); }}>
              Back to Sign In
            </button>
          </div>
        ) : (
          <>
            <h2 className="auth-title" id="auth-modal-title">
              {mode === "signin" ? "Sign In" : mode === "signup" ? "Create Account" : "Reset Password"}
            </h2>

            {mode !== "forgot" && CONTEXT_MESSAGES[contextReason] && (
              <p className="auth-context-message">{CONTEXT_MESSAGES[contextReason]}</p>
            )}

            <form className="auth-form" onSubmit={handleSubmit} noValidate>
              {mode === "signup" && (
                <div className="auth-field">
                  <label className="auth-label" htmlFor="auth-username">Username</label>
                  <input
                    id="auth-username"
                    ref={firstInputRef}
                    className="auth-input"
                    type="text"
                    value={username}
                    onChange={handleUsernameChange}
                    placeholder="your_username"
                    maxLength={20}
                    autoComplete="username"
                  />
                  {usernameHint}
                </div>
              )}

              <div className="auth-field">
                <label className="auth-label" htmlFor="auth-email">Email</label>
                <input
                  id="auth-email"
                  ref={mode !== "signup" ? firstInputRef : null}
                  className="auth-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              {mode !== "forgot" && (
                <div className="auth-field">
                  <label className="auth-label" htmlFor="auth-password">Password</label>
                  <input
                    id="auth-password"
                    className="auth-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === "signup" ? "Min. 8 characters" : "••••••••"}
                    required
                    autoComplete={mode === "signin" ? "current-password" : "new-password"}
                  />
                </div>
              )}

              {mode === "signup" && (
                <div className="auth-field">
                  <label className="auth-label" htmlFor="auth-confirm">Confirm Password</label>
                  <input
                    id="auth-confirm"
                    className="auth-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />
                </div>
              )}

              {mode === "signin" && (
                <button type="button" className="auth-forgot-link" onClick={() => setMode("forgot")}>
                  Forgot password?
                </button>
              )}

              {mode === "signup" && (
                <div className="policy-consent-field">
                  <input
                    id="auth-rules-accepted"
                    type="checkbox"
                    checked={rulesAccepted}
                    onChange={(e) => setRulesAccepted(e.target.checked)}
                  />
                  <label htmlFor="auth-rules-accepted">
                    I have read and agree to follow the{" "}
                    <Link to="/rules" target="_blank" rel="noopener noreferrer" className="account-inline-link">
                      Rules &amp; Code of Conduct
                    </Link>.
                  </label>
                </div>
              )}

              {error && (
                <div className="auth-error-block">
                  <p className="auth-error">{error}</p>
                  {showResend && (
                    resendSent ? (
                      <p className="auth-resend-sent">Confirmation email sent — check your inbox.</p>
                    ) : (
                      <>
                        {resendError && <p className="auth-resend-error">{resendError}</p>}
                        <button
                          type="button"
                          className="auth-resend-btn"
                          onClick={handleResend}
                          disabled={resendLoading}
                        >
                          {resendLoading ? "Sending…" : "Resend confirmation email"}
                        </button>
                      </>
                    )
                  )}
                </div>
              )}

              <button className="auth-submit" type="submit" disabled={loading}>
                {loading ? "Please wait…" : mode === "signin" ? "Sign In" : mode === "signup" ? "Create Account" : "Send Reset Link"}
              </button>
            </form>

            <p className="auth-switch">
              {mode === "forgot"
                ? "Remember your password? "
                : mode === "signin" ? "Don't have an account? " : "Already have an account? "
              }
              <button className="auth-toggle-link" onClick={() => setMode(mode === "signup" ? "signin" : mode === "forgot" ? "signin" : "signup")}>
                {mode === "signup" ? "Sign In" : mode === "forgot" ? "Sign In" : "Sign Up"}
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
